import { Link, useParams } from 'react-router-dom';
import type { FeedItemWithMeta } from '@news-assist/shared';
import { relativeTime } from '@news-assist/shared';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';

interface FeedItemProps {
  item: FeedItemWithMeta;
  orgPrefix?: string;
}

// Get urgency badge styling
function getUrgencyBadge(urgency?: number, urgencyLabel?: string) {
  if (!urgency || urgency > 3) return null; // Only show for flash (1), breaking (2), urgent (3)

  const styles: Record<number, { bg: string; text: string; label: string }> = {
    1: { bg: 'bg-red-600', text: 'text-white', label: 'FLASH' },
    2: { bg: 'bg-red-500', text: 'text-white', label: 'BREAKING' },
    3: { bg: 'bg-orange-500', text: 'text-white', label: 'URGENT' },
  };

  const style = styles[urgency];
  return style ? { ...style, label: urgencyLabel?.toUpperCase() || style.label } : null;
}

export function FeedItem({ item, orgPrefix: propOrgPrefix }: FeedItemProps) {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const orgPrefix = propOrgPrefix ?? (orgSlug ? `/${orgSlug}` : '');

  const isQuote = item.itemType === 'quote';
  const sourceCount = item.sourceCount || 1;
  const isMultiSource = sourceCount > 1;
  const urgencyBadge = getUrgencyBadge(item.urgency, item.urgencyLabel);
  const isHighUrgency = item.urgency && item.urgency <= 2;

  return (
    <article
      className={`p-4 hover:bg-gray-50 ${isMultiSource ? 'bg-green-50 border-l-4 border-green-500' : ''} ${isHighUrgency ? 'bg-red-50 border-l-4 border-red-500' : ''}`}
      data-testid="feed-item"
    >
      <div className="flex items-start gap-3">
        {/* Source badge(s) */}
        <div className="flex flex-col gap-1">
          {isMultiSource ? (
            <span
              className="px-2 py-1 text-xs font-medium bg-green-600 text-white rounded"
              data-testid="source-badge"
              title={item.sourceNames?.join(', ')}
            >
              {sourceCount} sources
            </span>
          ) : (
            <span
              className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded"
              data-testid="source-badge"
            >
              {item.sourceName || 'Unknown'}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Story link */}
          {item.storyTitle && item.storyId && (
            <Link
              to={`${orgPrefix}/story/${item.storyId}`}
              className="text-xs text-blue-600 hover:underline"
              data-testid="story-link"
            >
              {item.storyTitle}
            </Link>
          )}

          {/* Headline */}
          {item.question ? (
            <HoverCard openDelay={300} closeDelay={100}>
              <HoverCardTrigger asChild>
                <p className={`mt-1 text-gray-900 cursor-help ${isQuote ? 'italic' : ''}`}>
                  {isQuote && <span className="text-gray-400">"</span>}
                  {item.headline}
                  {isQuote && <span className="text-gray-400">"</span>}
                </p>
              </HoverCardTrigger>
              <HoverCardContent className="w-80" side="top">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">This claim answers:</p>
                  <p className="text-sm font-semibold">{item.question}</p>
                </div>
              </HoverCardContent>
            </HoverCard>
          ) : (
            <p className={`mt-1 text-gray-900 ${isQuote ? 'italic' : ''}`}>
              {isQuote && <span className="text-gray-400">"</span>}
              {item.headline}
              {isQuote && <span className="text-gray-400">"</span>}
            </p>
          )}

          {/* Summary */}
          {item.summary && (
            <p className="mt-1 text-sm text-gray-600">{item.summary}</p>
          )}

          {/* Meta */}
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
            {/* Urgency badge (only for flash/breaking/urgent) */}
            {urgencyBadge && (
              <span className={`px-1.5 py-0.5 rounded font-semibold ${urgencyBadge.bg} ${urgencyBadge.text}`}>
                {urgencyBadge.label}
              </span>
            )}
            {isQuote ? (
              <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                QUOTE
              </span>
            ) : (
              <>
                {/* Recency badge (only show if no urgency badge) */}
                {!urgencyBadge && (
                  <span
                    className={`px-1.5 py-0.5 rounded ${
                      (item.recencyScore ?? 0.5) >= 0.8
                        ? 'bg-red-100 text-red-700'
                        : (item.recencyScore ?? 0.5) < 0.5
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {(item.recencyScore ?? 0.5) >= 0.8 ? 'NEW' : (item.recencyScore ?? 0.5) < 0.5 ? 'CONTEXT' : 'DEVELOPING'}
                  </span>
                )}
                {item.claimType && (
                  <span
                    className={`px-1.5 py-0.5 rounded ${
                      item.claimType === 'statistic'
                        ? 'bg-blue-100 text-blue-700'
                        : item.claimType === 'prediction'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {item.claimType.toUpperCase()}
                  </span>
                )}
              </>
            )}
            {isMultiSource && item.sourceNames && (
              <span className="text-green-600" title={item.sourceNames.join(', ')}>
                Verified: {item.sourceNames.join(', ')}
              </span>
            )}
            <time dateTime={item.publishedAt}>{relativeTime(item.publishedAt)}</time>
          </div>
        </div>
      </div>
    </article>
  );
}
